import moment from 'moment';
import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  ALL_CATEGORY,
  MODAL_SIZES,
  COLORS,
  NEWS_CATEGORIES,
  NEWS_TAB_LABELS,
  NEWS_SOURCE_TO_SERVICES,
  FX24,
} from 'shared-modules/constants';
import { isNotAvailableNews } from 'shared-modules/utils/service';
import { useAccountInfo } from 'shared-modules/hooks';
import { useNewsData } from 'shared-modules/hooks/news';
import { NewsContext, NewsProvider } from 'shared-modules/contexts';
import { getNewsRequest, changeNewsTab, openCopyrightInfo } from '../../../../redux/actions';
import { Modal, Spin, Tabs, ItemButtons } from '../../../../components';
import CustomAccordion from '../../../../components/CustomAccordion';
import CustomButton from '../../../../components/CustomButton';
import styles from './manualTradeNews.module.scss';

const convertData = (data) =>
  data.map(({ id, time, category, title, body }) => ({
    key: id,
    title: (
      <div className={styles.accordionTitle}>
        <div className={styles.accordionDate}>{moment(time, 'YYYY/MM/DD HH:mm:ss').format('MM/DD HH:mm')}</div>
        <div className={styles.accordionCategories}>
          <div className={styles.tagInnerContainer}>
            <div className={styles.tagText}>{category}</div>
          </div>
        </div>
        <div className={styles.accordionNewsTitle}>{title}</div>
      </div>
    ),
    // eslint-disable-next-line react/no-danger
    body: <div className={styles.accordionBody} dangerouslySetInnerHTML={{ __html: body }} />,
  }));

const NewsContent = memo(() => {
  const [activeTab] = useContext(NewsContext);
  const [openedNews, setOpenedNews] = useState([]);
  const isLoading = useSelector((state) => state.manualTrade.loadingNewsData);
  const categories = NEWS_CATEGORIES[activeTab];
  const [selectedCategory, setCategory] = useState(ALL_CATEGORY.value);
  const newsData = useNewsData({ categories, selectedCategory });
  const displayedData = useMemo(() => convertData(newsData), [newsData]);

  const options = useMemo(() => {
    return categories.map(({ label, value }) => ({
      label,
      value,
      id: value, // use `value` as the unique id
      className: activeTab === FX24 ? styles.fx24tagButtonClass : styles.stock365tagButtonClass,
    }));
  }, [categories, activeTab]);

  useEffect(() => {
    setOpenedNews([]);
    setCategory(ALL_CATEGORY.value);
  }, [activeTab]);

  return (
    <div className={styles.container}>
      <div className={styles.controlBlock}>
        <ItemButtons options={options} activeId={selectedCategory} onChange={setCategory} />
      </div>
      <div className={styles.accordionWrapper}>
        {isLoading && (
          <div className={styles.emptyBlock}>
            <Spin className={styles.loader} />
          </div>
        )}
        {!isLoading && Boolean(displayedData.length) && (
          <CustomAccordion
            options={displayedData}
            defaultActiveKeys={openedNews}
            onChangeActiveKeys={setOpenedNews}
            cardHeaderStyle={styles.cardHeaderStyle}
            cardBodyStyle={styles.cardBodyStyle}
          />
        )}
        {!isLoading && !displayedData.length && <div className={styles.emptyBlock}>No data</div>}
      </div>
    </div>
  );
});

const News = ({ isOpen, closeModal }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useContext(NewsContext);
  const accountInfo = useAccountInfo();

  const tabItems = useMemo(() => {
    return Object.keys(NEWS_TAB_LABELS).map((tab) => {
      const services = NEWS_SOURCE_TO_SERVICES[tab];
      return {
        id: tab,
        value: NEWS_TAB_LABELS[tab],
        isVisuallyDisabled: services.every((service) => isNotAvailableNews(accountInfo[service])),
        children: <NewsContent />,
      };
    });
  }, [accountInfo]);

  const refreshNews = useCallback(() => {
    dispatch(getNewsRequest());
  }, [dispatch]);

  const handleCopyrightInfo = useCallback(
    () =>
      dispatch(
        openCopyrightInfo({
          isOverlap: true,
        }),
      ),
    [dispatch],
  );

  return (
    <Modal isTopAligned size={MODAL_SIZES.WIDTH_1024} isOpen={isOpen} closeModal={closeModal} title="ニュース">
      <div className={styles.iconBlock}>
        <CustomButton onClick={handleCopyrightInfo} color={COLORS.TRANSPARENT} width={24} isSmall>
          <i className={classNames('material-icons-outlined', styles.icon)}>info</i>
        </CustomButton>
        <CustomButton onClick={refreshNews} color={COLORS.TRANSPARENT} width={24} isSmall>
          <i className={classNames('material-icons', styles.icon)}>refresh</i>
        </CustomButton>
      </div>
      <Tabs containerClassName={styles.tabs} items={tabItems} activeKey={activeTab} onChange={setActiveTab} />
    </Modal>
  );
};

News.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
};

const TAB_SELECTOR = (state) => state.webManualTrade.newsActiveTabId;

const ManualTradeNews = memo(({ isOpen, closeModal }) => (
  <NewsProvider tabSelector={TAB_SELECTOR} changeTabAction={changeNewsTab}>
    <News isOpen={isOpen} closeModal={closeModal} />
  </NewsProvider>
));

ManualTradeNews.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
};

export default memo(ManualTradeNews);
